import { transpileBlock } from "./scratchTranspiler.js";
import { getDeclarations, sortTopLevelBlockKeys } from "./astEvaluator.js";

/**
 * Converts Scratch 3.0 project JSON into clean, standard, student-readable Python code.
 * Follows industry best practices (PictoBlox style):
 * - Empty workspace: sprite1 = Sprite('Sprite1')
 * - Loose blocks: sprite1.move(10)
 * - Event hats: def when_start(): ... sprite1.when_start(when_start) ... when_start()
 */
export const convertScratchToPython = (projectData, targetName = null) => {
  try {
    let projectObj = projectData;
    if (typeof projectData === "string") {
      if (!projectData.trim()) {
        projectObj = null;
      } else {
        try {
          projectObj = JSON.parse(projectData);
        } catch (e) {
          projectObj = null;
        }
      }
    }

    if (!projectObj || !projectObj.targets || projectObj.targets.length === 0) {
      return "sprite1 = Sprite('Sprite1')\n";
    }

    const allUsedMethods = new Set();
    const startCallables = [];
    const eventCounters = { flag: 0, key: 0, click: 0, broadcast: 0, backdrop: 0 };

    const stageTarget = projectObj.targets.find((t) => t.isStage);
    const seenSpriteNames = new Set();
    const spriteTargets = projectObj.targets.filter((t) => {
      if (t.isStage || t.isOriginal === false) return false;
      const nameKey = (t.name || "Sprite1").toLowerCase();
      if (seenSpriteNames.has(nameKey)) return false;
      seenSpriteNames.add(nameKey);
      return true;
    });

    // If targetName is provided, transpile ONLY that target (single-file sprite isolation)
    if (targetName && targetName !== "_all_") {
      const isTargetStage = targetName.toLowerCase() === "stage" || targetName === "_stage_";

      if (isTargetStage) {
        const outputLines = ["stage = Stage()"];
        const declLines = getDeclarations(stageTarget, stageTarget, "python");
        if (declLines.length > 0) {
          outputLines.push("", ...declLines);
        }
        outputLines.push("");
        const stageTopKeys = stageTarget && stageTarget.blocks
          ? sortTopLevelBlockKeys(stageTarget.blocks)
          : [];

        if (stageTopKeys.length > 0) {
          stageTopKeys.forEach((topKey) => {
            const ctx = {
              targetLang: "python",
              varName: "stage",
              indentLevel: 0,
              baseIndent: 0,
              lines: outputLines,
              usedMethods: allUsedMethods,
              startCallables,
              eventCounters,
            };
            transpileBlock(topKey, stageTarget.blocks, ctx);
            outputLines.push("");
          });
        }

        // Clean consecutive blank lines
        const cleanedLines = [];
        for (let i = 0; i < outputLines.length; i++) {
          const line = outputLines[i];
          if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
            continue;
          }
          cleanedLines.push(line);
        }

        const joined = cleanedLines.join("\n");
        const importLines = [];
        if (joined.includes("time.sleep") || joined.includes("time.time")) importLines.push("import time");
        if (joined.includes("random.randint") || joined.includes("random.choice")) importLines.push("import random");
        if (joined.includes("math.")) importLines.push("import math");

        const finalLines = [];
        if (importLines.length > 0) {
          finalLines.push(...importLines);
          finalLines.push("");
        }
        finalLines.push(...cleanedLines);

        if (startCallables.length > 0) {
          while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") finalLines.pop();
          finalLines.push("");
          startCallables.forEach((fn) => finalLines.push(`${fn}()`));
        }

        return finalLines.join("\n").trim() + "\n";
      } else {
        // Sprite target
        const target = spriteTargets.find(
          (t) => t.name === targetName || (t.name && t.name.toLowerCase() === targetName.toLowerCase())
        ) || spriteTargets[0];

        if (!target) {
          return "sprite1 = Sprite('Sprite1')\n";
        }

        const spriteName = target.name || "Sprite1";
        const varName = spriteName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || "sprite1";
        const outputLines = [`${varName} = Sprite('${spriteName}')`];
        const declLines = getDeclarations(target, stageTarget, "python");
        if (declLines.length > 0) {
          outputLines.push("", ...declLines);
        }

        const topBlockKeys = target.blocks
          ? sortTopLevelBlockKeys(target.blocks)
          : [];

        if (topBlockKeys.length > 0) {
          outputLines.push("");
          topBlockKeys.forEach((topKey) => {
            const ctx = {
              targetLang: "python",
              varName,
              indentLevel: 0,
              baseIndent: 0,
              lines: outputLines,
              usedMethods: allUsedMethods,
              startCallables,
              eventCounters,
            };
            transpileBlock(topKey, target.blocks, ctx);
            outputLines.push("");
          });
        }

        // Clean consecutive blank lines
        const cleanedLines = [];
        for (let i = 0; i < outputLines.length; i++) {
          const line = outputLines[i];
          if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
            continue;
          }
          cleanedLines.push(line);
        }

        const joined = cleanedLines.join("\n");
        const importLines = [];
        if (joined.includes("time.sleep") || joined.includes("time.time")) importLines.push("import time");
        if (joined.includes("random.randint") || joined.includes("random.choice")) importLines.push("import random");
        if (joined.includes("math.")) importLines.push("import math");

        const finalLines = [];
        if (importLines.length > 0) {
          finalLines.push(...importLines);
          finalLines.push("");
        }
        finalLines.push(...cleanedLines);

        if (startCallables.length > 0) {
          while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") finalLines.pop();
          finalLines.push("");
          startCallables.forEach((fn) => finalLines.push(`${fn}()`));
        }

        return finalLines.join("\n").trim() + "\n";
      }
    }

    // Default: transpile all targets combined (for downloads and standalone execution)
    // If no sprite exists, fallback
    if (spriteTargets.length === 0 && (!stageTarget || !stageTarget.blocks || Object.keys(stageTarget.blocks).length === 0)) {
      return "sprite1 = Sprite('Sprite1')\n";
    }

    const outputLines = [];
    let hasAnyBlocks = false;

    // Check if stage has top-level blocks
    const stageTopKeys = stageTarget && stageTarget.blocks
      ? sortTopLevelBlockKeys(stageTarget.blocks)
      : [];

    if (stageTopKeys.length > 0) {
      hasAnyBlocks = true;
      outputLines.push("stage = Stage()");
      const stageDeclLines = getDeclarations(stageTarget, stageTarget, "python");
      if (stageDeclLines.length > 0) {
        outputLines.push("", ...stageDeclLines);
      }
      outputLines.push("");
      stageTopKeys.forEach((topKey) => {
        const ctx = {
          targetLang: "python",
          varName: "stage",
          indentLevel: 0,
          baseIndent: 0,
          lines: outputLines,
          usedMethods: allUsedMethods,
          startCallables,
          eventCounters,
        };
        transpileBlock(topKey, stageTarget.blocks, ctx);
        outputLines.push("");
      });
    }

    // Process sprite targets
    spriteTargets.forEach((target, idx) => {
      const spriteName = target.name || `Sprite${idx + 1}`;
      const varName = spriteName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || `sprite${idx + 1}`;

      const topBlockKeys = target.blocks
        ? sortTopLevelBlockKeys(target.blocks)
        : [];

      outputLines.push(`${varName} = Sprite('${spriteName}')`);
      const declLines = getDeclarations(target, stageTarget, "python");
      if (declLines.length > 0) {
        outputLines.push("", ...declLines);
      }

      if (topBlockKeys.length > 0) {
        hasAnyBlocks = true;
        outputLines.push("");
        topBlockKeys.forEach((topKey) => {
          const ctx = {
            targetLang: "python",
            varName,
            indentLevel: 0,
            baseIndent: 0,
            lines: outputLines,
            usedMethods: allUsedMethods,
            startCallables,
            eventCounters,
          };
          transpileBlock(topKey, target.blocks, ctx);
          outputLines.push("");
        });
      }
    });

    // If no blocks across any targets, return clean sprite instantiation
    if (!hasAnyBlocks) {
      const declLines = spriteTargets.length > 0 ? getDeclarations(spriteTargets[0], stageTarget, "python") : [];
      const sName = (spriteTargets[0] && spriteTargets[0].name) || "Sprite1";
      const vName = sName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || "sprite1";
      if (declLines.length > 0) {
        return `${vName} = Sprite('${sName}')\n\n${declLines.join("\n")}\n`;
      }
      if (spriteTargets.length <= 1) {
        return `${vName} = Sprite('${sName}')\n`;
      }
      return (
        spriteTargets
          .map((t, idx) => {
            const sName = t.name || `Sprite${idx + 1}`;
            const vName = sName.toLowerCase().replace(/[^a-z0-9_]/g, "_") || `sprite${idx + 1}`;
            return `${vName} = Sprite('${sName}')`;
          })
          .join("\n") + "\n"
      );
    }

    // Clean up consecutive blank lines
    const cleanedLines = [];
    for (let i = 0; i < outputLines.length; i++) {
      const line = outputLines[i];
      if (line.trim() === "" && cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === "") {
        continue;
      }
      cleanedLines.push(line);
    }

    // Check imports needed
    const joined = cleanedLines.join("\n");
    const importLines = [];
    if (joined.includes("time.sleep") || joined.includes("time.time")) {
      importLines.push("import time");
    }
    if (joined.includes("random.randint") || joined.includes("random.choice")) {
      importLines.push("import random");
    }
    if (joined.includes("math.")) {
      importLines.push("import math");
    }

    const finalLines = [];
    if (importLines.length > 0) {
      finalLines.push(...importLines);
      finalLines.push("");
    }
    finalLines.push(...cleanedLines);

    // Call start event functions at the bottom for inline execution
    if (startCallables.length > 0) {
      while (finalLines.length > 0 && finalLines[finalLines.length - 1].trim() === "") {
        finalLines.pop();
      }
      finalLines.push("");
      startCallables.forEach((fn) => {
        finalLines.push(`${fn}()`);
      });
    }

    return finalLines.join("\n").trim() + "\n";
  } catch (err) {
    return `# Error transpiling Scratch to Python: ${err.message}`;
  }
};

/**
 * Converts Scratch 3.0 project JSON into a multi-file structure:
 * - Stage.py (for stage backdrop scripts)
 * - <SpriteName>.py for each sprite target
 * Returns { files: [...], activeTarget: string }
 */
export const convertScratchToPythonMultiFile = (projectData) => {
  try {
    let projectObj = projectData;
    if (typeof projectData === "string") {
      if (!projectData.trim()) projectObj = null;
      else {
        try {
          projectObj = JSON.parse(projectData);
        } catch (e) {
          projectObj = null;
        }
      }
    }

    if (!projectObj || !projectObj.targets || projectObj.targets.length === 0) {
      return {
        files: [
          {
            id: "stage",
            name: "Stage.py",
            targetName: "Stage",
            isStage: true,
            content: "stage = Stage()\n",
          },
          {
            id: "sprite1",
            name: "Sprite1.py",
            targetName: "Sprite1",
            isStage: false,
            content: "sprite1 = Sprite('Sprite1')\n",
          },
        ],
        activeTarget: "Sprite1",
      };
    }

    const stageTarget = projectObj.targets.find((t) => t.isStage);
    const seenNames = new Set();
    const spriteTargets = projectObj.targets.filter((t) => {
      if (t.isStage || t.isOriginal === false) return false;
      const nameKey = (t.name || "Sprite1").toLowerCase();
      if (seenNames.has(nameKey)) return false;
      seenNames.add(nameKey);
      return true;
    });
    const files = [];

    // 1. Stage.py
    const stageContent = convertScratchToPython(projectObj, "Stage");
    files.push({
      id: stageTarget ? stageTarget.id : "stage",
      name: "Stage.py",
      targetName: stageTarget ? stageTarget.name : "Stage",
      isStage: true,
      content: stageContent,
    });

    // 2. Sprite files
    spriteTargets.forEach((target, idx) => {
      const spriteName = target.name || `Sprite${idx + 1}`;
      const safeName = spriteName.replace(/[^a-zA-Z0-9_-]/g, "_") || `Sprite${idx + 1}`;
      const fileName = `${safeName}.py`;
      const content = convertScratchToPython(projectObj, spriteName);

      files.push({
        id: target.id || `sprite_${idx + 1}`,
        name: fileName,
        targetName: spriteName,
        isStage: false,
        content: content,
      });
    });

    // Default active target is the first sprite, or Stage if no sprites
    const activeTarget = spriteTargets.length > 0 ? spriteTargets[0].name : "Stage";

    return {
      files,
      activeTarget,
    };
  } catch (err) {
    return {
      files: [
        {
          id: "main",
          name: "main.py",
          targetName: "Main",
          isStage: false,
          content: `# Error generating multi-file project: ${err.message}`,
        },
      ],
      activeTarget: "Main",
    };
  }
};

/**
 * Generates a self-contained, 100% offline-runnable Python file for downloads.
 * Includes complete mock implementations for Sprites, Stage, Sound, Keyboard,
 * and Sensing functions so students can run the downloaded file directly with python.
 */
export const getStandalonePythonCode = (code) => {
  return `# =======================================================
# Practido Python Standalone Engine (Offline Runner)
# =======================================================
import sys
import time
import random
import math
import datetime

# Ensure safe console output across all platforms (handles Windows cp1252 & UTF-8)
if hasattr(sys.stdout, 'reconfigure'):
    try:
        sys.stdout.reconfigure(encoding='utf-8', errors='replace')
    except Exception:
        pass

class PractidoSprite:
    def __init__(self, name):
        self.name = name
        self.x = 0
        self.y = 0
        self.size = 100
        self.visible = True
        self.costume = 'default'
        self.direction = 90
        self.volume = 100
        self.layer = 1
        self.rotation_style = 'all around'
        self.drag_mode = 'draggable'
        self.effects = {}

    def move(self, steps):
        print(f"[{self.name}] Moved {steps} steps forward")

    def turn_right(self, degrees):
        self.direction = (self.direction + degrees) % 360
        print(f"[{self.name}] Turned right {degrees} degrees (Direction: {self.direction})")

    def turn_left(self, degrees):
        self.direction = (self.direction - degrees) % 360
        print(f"[{self.name}] Turned left {degrees} degrees (Direction: {self.direction})")

    def go_to(self, x, y):
        self.x, self.y = x, y
        print(f"[{self.name}] Went to x:{x}, y:{y}")

    def glide(self, secs, x, y):
        self.x, self.y = x, y
        print(f"[{self.name}] Glided to x:{x}, y:{y} over {secs}s")
        time.sleep(secs)

    def glide_to(self, secs, target):
        print(f"[{self.name}] Glided to {target} over {secs}s")
        time.sleep(secs)

    def point_towards(self, target):
        print(f"[{self.name}] Pointed towards {target}")

    def set_rotation_style(self, style):
        self.rotation_style = style

    def change_x(self, dx):
        self.x += dx
        print(f"[{self.name}] Changed X by {dx} (X: {self.x})")

    def change_y(self, dy):
        self.y += dy
        print(f"[{self.name}] Changed Y by {dy} (Y: {self.y})")

    def point_in_direction(self, dir):
        self.direction = dir
        print(f"[{self.name}] Pointed in direction {dir}")

    def bounce_if_on_edge(self):
        print(f"[{self.name}] Bounced on edge")

    def say(self, message):
        print(f"[{self.name}] Says: {message}")

    def think(self, message):
        print(f"[{self.name}] Thinks: {message}")

    def show(self):
        self.visible = True
        print(f"[{self.name}] Shown")

    def hide(self):
        self.visible = False
        print(f"[{self.name}] Hidden")

    def switch_costume(self, name):
        self.costume = name
        print(f"[{self.name}] Switched costume to {name}")

    def next_costume(self):
        print(f"[{self.name}] Next costume")

    def change_size(self, change):
        self.size += change
        print(f"[{self.name}] Changed size by {change}% (Size: {self.size})")

    def change_effect(self, effect, change):
        self.effects[effect] = self.effects.get(effect, 0) + change

    def set_effect(self, effect, value):
        self.effects[effect] = value

    def clear_graphic_effects(self):
        self.effects.clear()

    def go_to_layer(self, layer):
        self.layer = 1 if layer == 'front' else 0

    def change_layer(self, direction, num):
        self.layer += num if direction == 'forward' else -num

    def set_x(self, x):
        self.x = x
        print(f"[{self.name}] Set X to {x}")

    def set_y(self, y):
        self.y = y
        print(f"[{self.name}] Set Y to {y}")

    def set_size(self, size):
        self.size = size
        print(f"[{self.name}] Set size to {size}%")

    def say_for(self, message, secs):
        print(f"[{self.name}] Says: {message} ({secs}s)")
        time.sleep(secs)

    def think_for(self, message, secs):
        print(f"[{self.name}] Thinks: {message} ({secs}s)")
        time.sleep(secs)

    def change_sound_effect(self, effect, val):
        pass

    def set_sound_effect(self, effect, val):
        pass

    def clear_sound_effects(self):
        pass

    def play_sound_until_done(self, sound_name):
        print(f"[{self.name}] Played sound: {sound_name}")
        time.sleep(0.5)

    def when_start(self, handler):
        pass

    def when_key_pressed(self, key, handler):
        pass

    def when_clicked(self, handler):
        pass

    def when_broadcast_received(self, msg, handler):
        pass

    def when_greater_than(self, elem, val, handler):
        pass

    def when_start_as_clone(self, handler):
        self._clone_handler = handler

    def create_clone(self, target=None):
        print(f"[{self.name}] Created clone of {target or 'myself'}")
        if hasattr(self, '_clone_handler') and callable(self._clone_handler):
            self._clone_handler()

    def delete_this_clone(self):
        print(f"[{self.name}] Deleted clone")

    def delete_clone(self):
        print(f"[{self.name}] Deleted clone")

    def play_sound(self, sound_name):
        print(f"[{self.name}] Played sound: {sound_name}")

    def change_volume(self, change):
        self.volume = max(0, min(100, self.volume + change))

    def set_volume(self, vol):
        self.volume = max(0, min(100, vol))

    def is_touching(self, target):
        return False

    def is_touching_color(self, color):
        return False

    def color_is_touching_color(self, color1, color2):
        return False

    def distance_to(self, target):
        return 0

    def set_drag_mode(self, mode):
        self.drag_mode = mode

class PractidoStage:
    def __init__(self):
        self.backdrop = 'backdrop1'
        self.backdrop_number = 1
        self.backdrop_name = 'backdrop1'

    def switch_backdrop(self, name):
        self.backdrop = name
        self.backdrop_name = name
        print(f"[Stage] Switched backdrop to {name}")

    def next_backdrop(self):
        self.backdrop_number += 1
        print('[Stage] Next backdrop')

    def when_backdrop_switch(self, name, handler):
        pass

# Clean aliases
Sprite = PractidoSprite
Stage = PractidoStage

class PractidoSound:
    def __init__(self):
        self.volume = 100
        self.effects = {}

    def change_effect(self, effect, val):
        self.effects[effect] = self.effects.get(effect, 0) + val

    def set_effect(self, effect, val):
        self.effects[effect] = val

    def clear_effects(self):
        self.effects.clear()

class PractidoKeyboard:
    def __init__(self):
        self.pressed_keys = set()

    def is_pressed(self, key):
        return key in self.pressed_keys

    def on(self, key, handler):
        if callable(handler):
            handler()

stage = PractidoStage()
sound = PractidoSound()
keyboard = PractidoKeyboard()
mouse_x = 0
mouse_y = 0
mouse_down = False
timer_start = time.time()
user_answer = ""

def get_current_time(unit):
    now = datetime.datetime.now()
    units = {
        'YEAR': now.year, 'MONTH': now.month, 'DATE': now.day,
        'DAYOFWEEK': now.weekday() + 1, 'HOUR': now.hour,
        'MINUTE': now.minute, 'SECOND': now.second
    }
    return units.get(unit, 0)

def stop_all_sounds():
    print("Stopped all sounds")

def broadcast(msg):
    print(f"Broadcasted message: {msg}")

def broadcast_and_wait(msg):
    print(f"Broadcasted and waited for: {msg}")

# =======================================================
# Student Script
# =======================================================
${code}
`;
};
